import { supabase } from '../lib/supabase'
import { banco, type TabelaSync } from '../db/banco'
import { agoraISO } from '../db/tipos'
import { novoId } from '../db/novoId'
import { registrarFalhaDeGravacao } from './falhaDeGravacao'
import { classificarErro } from './classificarErro'
import { pedirSync } from './pedirSync'

// Campos que o servidor preenche — nunca sobem no push.
const CAMPOS_DO_SERVIDOR = ['tenant_id', 'criado_em', 'atualizado_em'] as const

// Depois de tres recusas ESPECIFICAS o app desiste daquele registro. Recusa
// especifica so: servidor fora do ar e queda de rede nao gastam tentativa,
// senao uma hora de instabilidade travaria registro bom (a sync roda de 15 em
// 15 minutos).
export const LIMITE_TENTATIVAS = 3

// Registra a falha e RELANCA. Relancar e essencial: DetalhePedido, AjustarItens,
// ConfirmarEntrega e useSalvamento ja tratam o erro por conta propria. O registro
// existe para os botoes que chamam isto com `void` e engoliriam a rejeicao.
export async function gravarLocalEEnfileirar<T extends { id: string }>(
  tabela: TabelaSync,
  registro: T,
): Promise<void> {
  try {
    await banco.transaction('rw', [banco.table(tabela), banco.outbox], async () => {
      await banco.table(tabela).put(registro)
      await banco.outbox.add({
        id: novoId(),
        tabela,
        registro: registro as unknown as Record<string, unknown>,
        criado_em: agoraISO(),
        tentativas: 0,
        ultimo_erro: null,
        ultimo_codigo: null,
        travado_em: null,
      })
    })
    // Ate esta linha o registro existe num lugar so: o aparelho dela. Pedir o
    // envio agora e o que encurta essa janela -- antes disto, o proximo envio
    // podia levar 15 minutos, e o que nao subiu vai embora junto com o celular.
    pedirSync()
  } catch (erro) {
    registrarFalhaDeGravacao(tabela, erro)
    throw erro
  }
}

export async function apagarLocalEEnfileirar(tabela: TabelaSync, id: string): Promise<void> {
  const registro = await banco.table(tabela).get(id)
  if (!registro) return
  const apagado = { ...registro, apagado_em: agoraISO() }
  await gravarLocalEEnfileirar(tabela, apagado)
}

// Um Salvar que grava VARIAS coisas (Politica Comercial da fornecedora,
// revisao de 24/09 da spec): tudo numa transacao so. As chamadas de
// gravarLocalEEnfileirar la dentro ENTRAM nesta transacao (Dexie aninha) --
// mesmo molde de excluirPedidoEmCascata. Se uma falhar, nada fica no aparelho
// nem na fila. ⚠ No SERVIDOR nao e tudo ou nada: a fila sobe registro por
// registro, e uma recusa aparece em "O que nao subiu", como sempre.
export async function gravarEmLote(
  gravacoes: ReadonlyArray<{ tabela: TabelaSync; registro: { id: string } }>,
): Promise<void> {
  if (gravacoes.length === 0) return
  const tabelas = [...new Set(gravacoes.map((g) => g.tabela))].map((t) => banco.table(t))
  await banco.transaction('rw', [...tabelas, banco.outbox], async () => {
    for (const g of gravacoes) await gravarLocalEEnfileirar(g.tabela, g.registro)
  })
}

// "Tentar de novo" devolve o ciclo INTEIRO ao registro, nao uma ultima
// tentativa solitaria: sem zerar `tentativas`, a primeira recusa travaria de
// novo na hora.
export async function destravar(id: string): Promise<void> {
  await banco.outbox.update(id, { travado_em: null, tentativas: 0 })
}

export async function empurrarOutbox(): Promise<{
  enviadas: number
  falhas: number
  travadas: number
}> {
  const entradas = await banco.outbox.orderBy('criado_em').toArray()
  let enviadas = 0
  let falhas = 0
  let travadas = 0

  for (const entrada of entradas) {
    if (entrada.travado_em) continue // o app ja desistiu deste

    const payload: Record<string, unknown> = { ...entrada.registro }
    for (const campo of CAMPOS_DO_SERVIDOR) delete payload[campo]
    const { error } = await supabase.from(entrada.tabela).upsert(payload)

    if (!error) {
      enviadas++
      await banco.outbox.delete(entrada.id)
      continue
    }

    falhas++
    const classe = classificarErro(error)

    if (classe === 'rede') {
      await banco.outbox.update(entrada.id, { ultimo_erro: error.message })
      break // sem rede: para e preserva FIFO
    }

    if (classe === 'temporario') {
      await banco.outbox.update(entrada.id, { ultimo_erro: error.message })
      continue // nao gasta tentativa
    }

    const tentativas = entrada.tentativas + 1
    const travou = tentativas >= LIMITE_TENTATIVAS
    if (travou) travadas++
    await banco.outbox.update(entrada.id, {
      tentativas,
      ultimo_erro: error.message,
      ultimo_codigo: (error as { code?: string }).code ?? null,
      travado_em: travou ? agoraISO() : null,
    })
  }

  return { enviadas, falhas, travadas }
}
